import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { adminApi } from '../api'
import { adminFixtureAccounts, useAdminFixturesFromQuery } from '../fixtures'
import { money } from '../../client/prediction-filter'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { ResponsiveTable } from '../../shared/ResponsiveTable'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { formatWhen, maskLogin, moneySigned, safeAdminError } from '../safe-ui'

type Tab = 'overview' | 'positions' | 'orders' | 'history' | 'activity'
type StatusAction = 'DISABLE' | 'REACTIVATE' | 'ARCHIVE'

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'positions', label: 'Open Positions' },
  { id: 'orders', label: 'Pending Orders' },
  { id: 'history', label: 'Trade History' },
  { id: 'activity', label: 'Account Activity' },
]

export function AdminTradingAccountDetailPage() {
  const { id } = useParams()
  const accountId = Number(id)
  const fixtures = useAdminFixturesFromQuery()
  const [tab, setTab] = useState<Tab>('overview')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [account, setAccount] = useState<Record<string, unknown> | null>(null)
  const [positions, setPositions] = useState<Array<Record<string, unknown>>>([])
  const [orders, setOrders] = useState<Array<Record<string, unknown>>>([])
  const [trades, setTrades] = useState<Array<Record<string, unknown>>>([])
  const [activities, setActivities] = useState<Array<Record<string, unknown>>>([])
  const [busy, setBusy] = useState(false)
  const [confirmAction, setConfirmAction] = useState<StatusAction | null>(null)
  const [balAction, setBalAction] = useState<'CREDIT' | 'DEBIT'>('CREDIT')
  const [balAmount, setBalAmount] = useState('')
  const [balReason, setBalReason] = useState('')
  const [balNotice, setBalNotice] = useState('')
  const [balConfirm, setBalConfirm] = useState(false)

  const load = useCallback(async () => {
    if (!Number.isFinite(accountId)) {
      setError('Invalid account id')
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      if (fixtures) {
        const fx = adminFixtureAccounts.find((a) => a.id === accountId) || adminFixtureAccounts[0]
        setAccount({
          id: fx.id,
          mt5_login_masked: maskLogin(fx.mt5_login),
          server: fx.server,
          mode: fx.mode,
          currency: fx.currency,
          balance: fx.balance,
          equity: fx.equity,
          status: fx.status,
          sync_state: 'FIXTURE',
          last_sync_at: fx.created_at || null,
          client_id: fx.client_id,
        })
        setPositions([])
        setOrders([])
        setTrades([])
        setActivities([])
        return
      }

      // Overview must not wait on trade-history/activity — those MT5 walks
      // hold the bridge and make summary return "temporarily unavailable".
      const [sum, pos, pend] = await Promise.all([
        adminApi.accountSummary(accountId),
        adminApi.accountOpenPositions(accountId).catch(() => ({ positions: [] })),
        adminApi.accountPendingOrders(accountId).catch(() => ({ orders: [] })),
      ])

      const acc = { ...(sum.account || {}) }
      // Never surface raw login — prefer masked helper
      if (acc.mt5_login != null && !acc.mt5_login_masked) {
        acc.mt5_login_masked = maskLogin(acc.mt5_login as number | string)
      }
      delete acc.mt5_login
      delete acc.leverage
      delete acc.group
      delete acc.password
      delete acc.investor_password

      setAccount(acc)
      setPositions(pos.positions || [])
      setOrders(pend.orders || [])
    } catch (err) {
      setError(safeAdminError(err, 'Unable to load trading account'))
    } finally {
      setLoading(false)
    }
  }, [accountId, fixtures])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (fixtures || !Number.isFinite(accountId)) return
    if (tab !== 'history' && tab !== 'activity') return
    let cancelled = false
    void (async () => {
      try {
        if (tab === 'history') {
          const hist = await adminApi.accountTradeHistory(accountId, {
            page: 1,
            pageSize: 50,
          })
          if (!cancelled) setTrades(hist.trades || [])
        } else {
          const act = await adminApi.accountActivity(accountId, {
            page: 1,
            pageSize: 50,
          })
          if (!cancelled) setActivities(act.activities || [])
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            safeAdminError(
              err,
              tab === 'history'
                ? 'Unable to load trade history'
                : 'Unable to load account activity',
            ),
          )
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [tab, accountId, fixtures])

  async function applyStatus(action: StatusAction, reason: string) {
    if (fixtures) return
    setBusy(true)
    setConfirmAction(null)
    try {
      await adminApi.setAccountStatus(accountId, action, reason)
      await load()
    } catch (err) {
      setError(safeAdminError(err, 'Status update failed'))
    } finally {
      setBusy(false)
    }
  }

  async function applyBalance() {
    if (fixtures) return
    setBusy(true)
    setBalConfirm(false)
    setBalNotice('')
    setError('')
    try {
      const res = await adminApi.accountBalanceAdjust(accountId, {
        action: balAction,
        amount: Number(balAmount),
        reason: balReason.trim(),
      })
      setBalNotice(
        `${res.action} $${Number(res.amount).toFixed(2)} · ${res.balanceBefore ?? '—'} → ${res.balanceAfter ?? '—'}`,
      )
      setBalAmount('')
      setBalReason('')
      await load()
    } catch (err) {
      setError(safeAdminError(err, 'Balance update failed'))
    } finally {
      setBusy(false)
    }
  }

  const q = fixtures ? '?fixtures=1' : ''
  const currency = String(account?.currency || 'USD')
  const status = String(account?.status || '').toUpperCase()
  const loginDisplay = String(account?.mt5_login_masked || '—')

  return (
    <section className="cp-page">
      <PageHeader eyebrow="Trading account" title={`Account #${accountId}`}>
        <p className="cp-muted">
          <Link to={`/admin/trading-accounts${q}`}>← Trading accounts</Link>
          {account?.client_id ? (
            <>
              {' · '}
              <Link to={`/admin/clients/${String(account.client_id)}${q}`}>Client</Link>
            </>
          ) : null}
        </p>
      </PageHeader>

      {fixtures ? (
        <StateBlock tone="warn" title="Fixture mode · visual review only">
          Status actions are disabled in fixture preview.
        </StateBlock>
      ) : null}

      {loading ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading…
        </p>
      ) : null}
      {error ? <p className="cp-error">{error}</p> : null}

      <div className="ap-tabs" role="tablist" aria-label="Account sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`ap-tab${tab === t.id ? ' is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' ? (
        <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
          <h2>Overview</h2>
          <dl className="cp-stat-grid">
            <div>
              <dt>Masked login</dt>
              <dd>{account ? loginDisplay : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Server</dt>
              <dd>{account ? String(account.server || '—') : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Mode</dt>
              <dd>{account ? String(account.mode || '—') : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Currency</dt>
              <dd>{account ? currency : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{account ? (status || '—') : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Balance</dt>
              <dd>{account ? money(account.balance as number) : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Equity</dt>
              <dd>{account ? money(account.equity as number) : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Floating P/L</dt>
              <dd>{account ? moneySigned(account.floating_profit as number, currency) : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Free margin</dt>
              <dd>{account ? money(account.free_margin as number) : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Sync state</dt>
              <dd>{account ? String(account.sync_state || '—') : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Last updated</dt>
              <dd>
                {account
                  ? formatWhen(String(account.last_sync_at || account.updated_at || ''))
                  : loading
                    ? 'Loading…'
                    : '—'}
              </dd>
            </div>
          </dl>

          {account ? (
            <>
              <div className="cp-platform-actions">
                <Button
                  variant="secondary"
                  disabled={busy || fixtures || status === 'DISABLED'}
                  onClick={() => setConfirmAction('DISABLE')}
                >
                  Disable
                </Button>
                <Button
                  variant="secondary"
                  disabled={busy || fixtures || status === 'ACTIVE'}
                  onClick={() => setConfirmAction('REACTIVATE')}
                >
                  Reactivate
                </Button>
                <Button
                  variant="secondary"
                  disabled={busy || fixtures || status === 'ARCHIVED'}
                  onClick={() => setConfirmAction('ARCHIVE')}
                >
                  Archive
                </Button>
              </div>
              <p className="cp-note">Leverage, group, and passwords are never shown.</p>

              <h3 style={{ marginTop: '1.5rem' }}>Manual MT5 balance</h3>
              <p className="cp-muted">
                Main Admin credit (add) or debit (withdraw) on this account via bridge DealerBalance.
              </p>
              {balNotice ? <p className="cp-ok">{balNotice}</p> : null}
              <div className="cp-auth__form" style={{ maxWidth: '28rem' }}>
                <label className="cp-field">
                  <span className="cp-field__label">Action</span>
                  <select
                    value={balAction}
                    onChange={(e) => setBalAction(e.target.value as 'CREDIT' | 'DEBIT')}
                    disabled={fixtures || busy}
                  >
                    <option value="CREDIT">Add balance (CREDIT)</option>
                    <option value="DEBIT">Withdraw balance (DEBIT)</option>
                  </select>
                </label>
                <label className="cp-field">
                  <span className="cp-field__label">Amount (USD)</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={balAmount}
                    onChange={(e) => setBalAmount(e.target.value)}
                    disabled={fixtures || busy}
                  />
                </label>
                <label className="cp-field">
                  <span className="cp-field__label">Reason</span>
                  <input
                    value={balReason}
                    onChange={(e) => setBalReason(e.target.value)}
                    disabled={fixtures || busy}
                    minLength={3}
                  />
                </label>
                <Button
                  type="button"
                  disabled={
                    fixtures ||
                    busy ||
                    !balAmount ||
                    Number(balAmount) <= 0 ||
                    balReason.trim().length < 3
                  }
                  onClick={() => setBalConfirm(true)}
                >
                  Review balance change
                </Button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {tab === 'positions' ? (
        <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
          <h2>Open positions</h2>
          <ResponsiveTable
            rows={positions}
            rowKey={(p, i) => String(p.ticket || i)}
            empty={loading ? 'Loading…' : 'No open positions.'}
                columns={[
                  { key: 'ticket', header: 'Ticket', render: (p) => String(p.ticket || '—') },
                  { key: 'symbol', header: 'Symbol', render: (p) => String(p.symbol || '—') },
                  { key: 'side', header: 'Side', render: (p) => String(p.side || '—') },
                  { key: 'volume', header: 'Volume', render: (p) => String(p.volume ?? '—') },
                  { key: 'open', header: 'Open', render: (p) => money(p.open_price as number) },
                  { key: 'current', header: 'Current', render: (p) => money(p.current_price as number) },
                  {
                    key: 'profit',
                    header: 'Profit',
                    render: (p) => moneySigned(p.profit as number, currency),
                  },
              ]}
            />
        </div>
      ) : null}

      {tab === 'orders' ? (
        <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
          <h2>Pending orders</h2>
          <ResponsiveTable
            rows={orders}
            rowKey={(o, i) => String(o.ticket || i)}
            empty={loading ? 'Loading…' : 'No pending orders.'}
                columns={[
                  { key: 'ticket', header: 'Ticket', render: (o) => String(o.ticket || '—') },
                  { key: 'symbol', header: 'Symbol', render: (o) => String(o.symbol || '—') },
                  { key: 'type', header: 'Type', render: (o) => String(o.type || o.side || '—') },
                  { key: 'volume', header: 'Volume', render: (o) => String(o.volume ?? '—') },
                  { key: 'price', header: 'Price', render: (o) => money(o.price as number) },
              ]}
            />
        </div>
      ) : null}

      {tab === 'history' ? (
        <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
          <h2>Trade history</h2>
          <ResponsiveTable
            rows={trades}
            rowKey={(t, i) => String(t.ticket || i)}
            empty={loading ? 'Loading…' : 'No closed trades in range.'}
                columns={[
                  { key: 'ticket', header: 'Ticket', render: (t) => String(t.ticket || '—') },
                  { key: 'symbol', header: 'Symbol', render: (t) => String(t.symbol || '—') },
                  { key: 'side', header: 'Side', render: (t) => String(t.side || '—') },
                  { key: 'volume', header: 'Volume', render: (t) => String(t.volume ?? '—') },
                  {
                    key: 'profit',
                    header: 'Profit',
                    render: (t) => moneySigned(t.profit as number, currency),
                  },
                  {
                    key: 'close',
                    header: 'Closed',
                    render: (t) => formatWhen(String(t.close_time || '')),
                  },
              ]}
            />
        </div>
      ) : null}

      {tab === 'activity' ? (
        <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
          <h2>Account activity</h2>
          <ResponsiveTable
            rows={activities}
            rowKey={(a, i) => String(a.ticket || a.timestamp || i)}
            empty={loading ? 'Loading…' : 'No account activity in range.'}
                columns={[
                  {
                    key: 'when',
                    header: 'Timestamp',
                    render: (a) => formatWhen(String(a.timestamp || '')),
                  },
                  { key: 'type', header: 'Type', render: (a) => String(a.type || a.event_type || '—') },
                  {
                    key: 'amount',
                    header: 'Amount',
                    render: (a) => moneySigned(a.amount as number, currency),
                  },
                  {
                    key: 'desc',
                    header: 'Description',
                    render: (a) => String(a.description || a.comment || '—'),
                  },
              ]}
            />
        </div>
      ) : null}

      {!loading && !account && !error ? (
        <StateBlock tone="warn" title="Not found">
          Trading account unavailable.
        </StateBlock>
      ) : null}

      <ConfirmDialog
        open={confirmAction !== null}
        title={
          confirmAction === 'DISABLE'
            ? 'Disable trading account?'
            : confirmAction === 'ARCHIVE'
              ? 'Archive trading account?'
              : 'Reactivate trading account?'
        }
        body={
          <p>
            This uses the existing admin account status API for account #{accountId}. Provide a reason to continue.
          </p>
        }
        confirmLabel={confirmAction || 'Confirm'}
        danger={confirmAction === 'DISABLE' || confirmAction === 'ARCHIVE'}
        busy={busy}
        requireReason
        onCancel={() => setConfirmAction(null)}
        onConfirm={(reason) => {
          if (confirmAction) void applyStatus(confirmAction, reason)
        }}
      />

      <ConfirmDialog
        open={balConfirm}
        title={`${balAction === 'CREDIT' ? 'Add' : 'Withdraw'} $${Number(balAmount || 0).toFixed(2)}?`}
        body={
          <p>
            Account #{accountId} ({loginDisplay}). Real MT5 DealerBalance via bridge. Reason: {balReason}
          </p>
        }
        confirmLabel={balAction === 'CREDIT' ? 'Credit now' : 'Debit now'}
        danger={balAction === 'DEBIT'}
        busy={busy}
        onCancel={() => setBalConfirm(false)}
        onConfirm={() => void applyBalance()}
      />
    </section>
  )
}
